import { ActionConfig } from "@/types/products/product-config";
import { Ref } from "vue";
import { ProductFilter } from "@/composables/filters/product-filter";
import { CategoryFilter } from "@/composables/filters/category-filter";
import { PagingFilter } from "@/composables/filters/paging-filter";
import { useCoursesStore } from "@/stores/Courses";
import { type LabelService, useLabelService } from "@/composables/label-service";

import pinia from "@/plugins/PiniaPlugin";

import * as tmi from "@/constants";

export type ProductDefinition = {
    actionsMap: ReadonlyMap< string, ActionConfig >,
    initialized: Ref< boolean >,
    initialView: string,
    logoPath: Ref< string | undefined >,
    darkLogoPath: Ref< string | undefined >,
    portfolioActionsMap?: ReadonlyMap< string, ActionConfig >,
    selectedView: Ref< string >
};

const labelService: LabelService = useLabelService();

const articleTutorialsDisplayText: string = 'Tutorials';
const bonusReportsDisplayText: string = 'Bonus Reports';
const introductionVideoDisplayText: string = 'Introduction';
const majorIndexBreadthDisplayText: string = 'Indexes';
const modelPortfolioDisplayText: string = 'Model Portfolio';
const fiveStarModelPortfolioDisplayText: string = '5-Star Portfolio';
const nyseBpiDisplayText: string = 'NYSE BPI';
// const stockReplacementStrategyCalculatorDisplayText: string = 'SRS Calculator';
const portfolioBuilderDisplayText: string = 'My Portfolio';
const positionSizeCalculatorDisplayText: string = 'Position Size Calc';
const productAlertsDisplayText: string = 'Alerts';
const fiveStarPortfolioAlertsDisplayText: string = '5-Star Alerts';
const productArticlesDisplayText: string = 'Articles';
const productLessonsDisplayText: string = 'Lessons';
const rsMatrixDisplayText: string = 'RS Matrix';
const sectorsDisplayText: string = 'Sectors';
const sectorAlertDisplayText: string = 'Sector Alerts';

const sectorSpotlightDisplayText = labelService.label( 'lblSectorSpotlight' );
const sectorSpotlightSearchPlaceholderText = `Search ${ sectorSpotlightDisplayText }`;

const sectorTreeDisplayText: string = 'Sector Tree';
const spotlightVideoDisplayText: string = 'Spotlight Videos';
const spreadCalculatorDisplayText: string = 'Spread Calculator';
const tradeAlertsScannerDisplayText: string = 'Scanner History';
const webinarDisplayText: string = 'Webinars';
const welcomeVideoDisplayText: string = 'Welcome Video';

// Portfolio Label Keys
export const openPositionsTitle: string = 'Open Positions';
export const closedPositionsTitle: string = 'Closed Positions';
export const openedPositionKey: string = 'opened';
export const closedPositionKey: string = 'closed';
export const initiatedKey: string = 'initiated';

// Portfolio Column Keys
export const actionsColumnKey: string = 'actions';
export const bpiStatusColumnKey: string = 'bpi-status';
export const buyUnderColumnKey: string = 'buy_under';
export const closedColumnKey: string = closedPositionKey;
export const closedStockPriceColumnKey: string = 'closed_stock_price';
export const closedOptionsPriceColumnKey: string = 'closed_options_price';
export const initiatedColumnKey: string = 'initiated';
export const lastCheckPointColumnKey: string = 'last-check-point';
export const lastCloseColumnKey: string = 'last-close';
export const marketRelativeStrengthColumnKey: string = 'market-rs';
export const maxGainColumnKey: string = 'max_gain';
export const monthEndGainColumnKey: string = 'm_e_gain';
export const noteColumnKey: string = 'note';
export const openedStockPriceColumnKey: string = 'opened_stock_price';
export const openedOptionsPriceColumnKey: string = 'opened_options_price';
export const optionsGainsColumnKey: string = 'options_gains';
export const peerRelativeStrengthColumnKey: string = 'peer-rs';
export const priceTargetColumnKey: string = 'price_target';
export const stopLossColumnKey: string = 'stop_loss';
export const sectorColumnKey: string = 'sector';
export const sectorRelativeStrengthColumnKey: string = 'sector-rs';
export const stanceColumnKey: string = 'stance';
export const stockGainsColumnKey: string = 'stock_gains';
export const symbolColumnKey: string = 'symbol';

// CONTENT KEYS
export const articleKey: string = 'articles';

export const bonusReportKey: string = 'bonus-reports';

export const webinarKey: string = 'webinars';

// Alias keys for Articles
export const sectorSpotlightKey: string = 'sector-spotlight';
export const sectorFocusSpotlightKey: string = 'sector-focus-spotlight';
export const spotlightVideosKey: string = 'spotlight-videos';

export const courseKey: string = 'course';

export const contentWrapperKey: string = 'content-wrapper';
export const etfListingKey: string = 'etf-listing';
export const lessonsKey: string = 'lessons';
export const cptLessonsKey: string = 'cpt-lessons';
export const dmiLessonsKey: string = 'dmi-lessons';
export const dmiArticlesKey: string = 'dmi-articles';
export const dmtLessonsKey: string = 'dmt-lessons';
export const psLessonsKey: string = 'ps-lessons';
export const osLessonsKey: string = 'os-lessons';
export const sfLessonsKey: string = 'sf-lessons';
export const spLessonsKey: string = 'sp-lessons';
export const tamLessonsKey: string = 'tam-lessons';
export const tspLessonsKey: string = 'tsp-lessons';
export const majorIndexBreadthKey: string = 'major-index-breadth';
export const modelPortfolioKey: string = 'model-portfolio';
export const fiveStarModelPortfolioKey: string = '5-star-model-portfolio';
export const tspModelPortfolioKey: string = 'tsp-model-portfolio';
export const notFoundKey: string = '404';
export const nyseBpiKey: string = 'nyse-bpi';
// export const stockReplacementStrategyCalculatorKey: string = 'srs-calc';
export const portfolioBuilderKey: string = 'portfolio-builder';
export const positionSizeCalculatorKey: string = 'pos-size';
export const relativeStrengthMatrixKey: string = 'rs-matrix';
export const scannerKey: string = 'scanner';
export const sectorsKey: string = 'sectors';
export const sectorAlertsKey: string = 'sector-alerts';
export const sectorTreeKey: string = 'sector-tree';
export const shadowSpikeAlertKey: string = 'shadow-spike-alert';
export const spotlightVideoKey: string = 'spotlight-videos';
export const tradeAlertKey: string = 'trade-alerts';
export const fiveStarTradeAlertKey: string = '5-star-trade-alerts';
export const tradeAlertSearchKey: string = 'trade-alert-search';
export const tradeOfTheDayKey: string = 'trade-of-the-day';
// export const tspPortfolioKey: string = 'tsp-portfolio';
export const tutorialsKey: string = 'tutorials';
// export const underConstructionKey: string = 'under-construction';
export const osVimeoKey: string = 'os-vimeo-wrapper';
export const mttVimeoKey: string = 'mtt-vimeo-wrapper';
export const tamVimeoKey: string = 'tam-vimeo-wrapper';
export const vimeoKey: string = 'vimeo-wrapper';

// Store refresh keys
export const syncThrottlingKey: string = 'sync-throttle';
export const authorsSyncKey: string = 'authors-sync-meta';
export const categoriesSyncKey: string = 'categories-sync-meta';
export const industrySectorsSyncKey: string = 'industry-sectors-sync-meta';
export const productsSyncKey: string = 'products-sync-meta';
export const sectorsSyncKey: string = 'sectors-sync-meta';
export const tagsSyncKey: string = 'tags-sync-meta';
export const tickersSyncKey: string = 'tickers-sync-meta';
export const simplePagesSyncKey: string = 'simple-pages-sync-meta';
export const sectorHistorySyncKey: string = 'sector-history-sync-meta';
export const articlesSyncKey: string = 'articles-sync-meta';
export const bonusReportsSyncKey: string = 'bonus-reports-sync-meta';
export const tradeAlertsSyncKey: string = 'trade-alerts-sync-meta';
export const webinarsSyncKey: string = 'webinars-sync-meta';

// Context Key Constants
export const categoryContextKey: string = 'category';
export const contentSlugContextKey: string = 'content-slug';
export const courseSlugContextKey: string = 'course-slug';
export const placeholderContextKey: string = 'placeholder';
export const titleContextKey: string = 'title';
export const vimeoIdContextKey: string = 'vimeo-id';

export const articleTutorialsActionConfig: ActionConfig = {
    displayText: articleTutorialsDisplayText,
    componentKey: articleKey,
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter.prePopulateSelectedCategories( [ tutorialsKey ] );
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    },
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Tutorials' ],
        [ placeholderContextKey, 'Search Tutorials' ],
        [ categoryContextKey, tutorialsKey ]
    ] )
};

export const introductionVideoActionConfig: ActionConfig = {
    displayText: introductionVideoDisplayText,
    componentKey: vimeoKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Introduction to Options' ],
        [ vimeoIdContextKey, 788032335 ]
    ] )
};
export const modelPortfolioActionConfig: ActionConfig = { displayText: modelPortfolioDisplayText, componentKey: modelPortfolioKey };
export const fiveStarModelPortfolioActionConfig: ActionConfig = { displayText: fiveStarModelPortfolioDisplayText, componentKey: fiveStarModelPortfolioKey };

export const majorIndexBreadthActionConfig: ActionConfig = { displayText: majorIndexBreadthDisplayText, componentKey: majorIndexBreadthKey };
export const nyseBpiActionConfig: ActionConfig = { displayText: nyseBpiDisplayText, componentKey: nyseBpiKey };
// export const stockReplacementStrategyCalculatorActionConfig: ActionConfig = { displayText: stockReplacementStrategyCalculatorDisplayText, componentKey: stockReplacementStrategyCalculatorKey };
export const portfolioBuilderActionConfig: ActionConfig = { displayText: portfolioBuilderDisplayText, componentKey: portfolioBuilderKey };
export const positionSizeCalculatorActionConfig: ActionConfig = { displayText: positionSizeCalculatorDisplayText, componentKey: positionSizeCalculatorKey };
export const productAlertActionConfig: ActionConfig = {
    displayText: productAlertsDisplayText,
    componentKey: tradeAlertKey,
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter?.resetSelectedCategories();
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const fiveStarAlertActionConfig: ActionConfig = {
    displayText: fiveStarPortfolioAlertsDisplayText,
    componentKey: tradeAlertKey,
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter?.resetSelectedCategories();
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const productArticlesActionConfig: ActionConfig = {
    displayText: productArticlesDisplayText,
    componentKey: articleKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Articles & Commentary' ],
        [ placeholderContextKey, 'Search Articles' ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter?.resetSelectedCategories();
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const productBonusReportsActionConfig: ActionConfig = {
    displayText: bonusReportsDisplayText,
    componentKey: bonusReportKey,
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter?.resetSelectedCategories();
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const cptProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'CheckPoint Trader Accelerated Program' ],
        [ courseSlugContextKey, 'checkpoint-trader-accelerated-program' ]
    ] ),
    reset: ( productSlug: string ) => {
        const courseStore = useCoursesStore( pinia );
        courseStore.setSelectedCourseByProductSlug( productSlug );
    }
};

export const dmiProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Welcome to Dark Money Indicator' ],
        [ courseSlugContextKey, 'dark-money-indicator-dmi' ]
    ] )
};

export const dmtProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Welcome to Deep Market Trader' ],
        [ courseSlugContextKey, 'welcome-to-deep-market-trader' ]
    ] )
};

export const osProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Options Soup' ],
        [ courseSlugContextKey, 'options-soup-oso' ]
    ] )
};

export const psProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Profit Skimmer: Intro Series' ],
        [ courseSlugContextKey, 'profit-skimmer-intro-series' ]
    ] )
};

export const sfProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Sector Focus: Intro to Sectors Series' ],
        [ courseSlugContextKey, 'sector-focus-intro-to-sectors-series' ]
    ] )
};

export const spProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Welcome to Sector Prophets' ],
        [ courseSlugContextKey, 'welcome-to-sector-prophets' ]
    ] )
};

export const tamProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ courseSlugContextKey, 'technical-analysis-millionaire-tam' ],
        [ titleContextKey, 'Technical Analysis Millionaire (TAM)' ]
    ] )
};

export const tspProductCourseActionConfig: ActionConfig = {
    displayText: productLessonsDisplayText,
    componentKey: lessonsKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Crypto Quantum Leap Course' ],
        [ courseSlugContextKey, 'crypto-quantum-leap-course' ]
    ] )
};

export const relativeStrengthMatrixActionConfig: ActionConfig = { displayText: rsMatrixDisplayText, componentKey: relativeStrengthMatrixKey };
export const sectorsActionConfig: ActionConfig = { displayText: sectorsDisplayText, componentKey: sectorsKey };
export const sectorAlertsActionConfig: ActionConfig = { displayText: sectorAlertDisplayText, componentKey: sectorAlertsKey };

export const sectorSpotlightActionConfig: ActionConfig = {
    displayText: sectorSpotlightDisplayText,
    componentKey: articleKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, sectorSpotlightDisplayText ],
        [ placeholderContextKey, sectorSpotlightSearchPlaceholderText ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter.prePopulateSelectedCategories( [ sectorSpotlightKey ] );
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const sectorFocusSpotlightActionConfig: ActionConfig = {
    displayText: sectorSpotlightDisplayText,
    componentKey: articleKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, sectorSpotlightDisplayText ],
        [ placeholderContextKey, sectorSpotlightSearchPlaceholderText ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter.prePopulateSelectedCategories( [ sectorFocusSpotlightKey ] );
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const sectorTreeActionConfig: ActionConfig = {
    displayText: sectorTreeDisplayText,
    componentKey: contentWrapperKey,
    ctx: new Map< string, any >( [
        [ contentSlugContextKey, 'sector-tree' ]
    ] )
};

export const spotlightVideoActionConfig: ActionConfig = {
    displayText: spotlightVideoDisplayText,
    componentKey: articleKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Spotlight Videos' ],
        [ placeholderContextKey, 'Search Spotlight Videos' ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter.prePopulateSelectedCategories( [ spotlightVideoKey ] );
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const spreadCalculatorActionConfig: ActionConfig = { displayText: spreadCalculatorDisplayText, componentKey: tmi.SpreadCalculatorKey };
export const tamIntroductionVideoActionConfig: ActionConfig = {
    displayText: introductionVideoDisplayText,
    componentKey: vimeoKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Welcome to the Technical Analysis Millionaire Course' ],
        [ vimeoIdContextKey, 838414602 ]
    ] )
};
export const tradeAlertsScannerActionConfig: ActionConfig = {
    displayText: tradeAlertsScannerDisplayText,
    componentKey: scannerKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Shadow Spike Alerts' ],
        [ placeholderContextKey, 'Search Shadow Spike Alerts' ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter.prePopulateSelectedCategories( [ shadowSpikeAlertKey ] );
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const tspModelPortfolioActionConfig: ActionConfig = {
    displayText: modelPortfolioDisplayText,
    componentKey: contentWrapperKey,
    ctx: new Map< string, any >( [
        [ contentSlugContextKey, 'tsp-portfolio-table' ]
    ] )
};

export const webinarActionConfig: ActionConfig = {
    displayText: webinarDisplayText,
    componentKey: webinarKey,
    ctx: new Map< string, any >( [
        [ placeholderContextKey, 'Search Webinars' ]
    ] ),
    reset: ( productSlug: string, productFilter: ProductFilter, categoryFilter: CategoryFilter, pagingFilter: PagingFilter ) => {
        categoryFilter?.resetSelectedCategories();
        pagingFilter?.resetPageNumber();
        productFilter.setSelectedProduct( productSlug );
    }
};

export const welcomeVideoActionConfig: ActionConfig = {
    displayText: welcomeVideoDisplayText,
    componentKey: vimeoKey,
    ctx: new Map< string, any >( [
        [ titleContextKey, 'Monthly Trend Trader - Welcome Video' ],
        [ vimeoIdContextKey, 529362272 ]
    ] )
};

export const cptProductKey: string = 'checkpoint-trader';
export const dmiProductKey: string = 'dark-money-indicator';
export const dmtProductKey: string = 'deep-market-trader';
export const mttProductKey: string = 'monthly-trend-trader';
export const osProductKey: string = 'options-soup';
export const psProductKey: string = 'profit-skimmer';
export const sfProductKey: string = 'sector-focus';
export const spProductKey: string = 'sector-prophets';
export const tamProductKey: string = 'technical-analysis-millionaire';
export const tspProductKey: string = 'true-sovereign-profits';

// TODO: find a more flexible means of mapping this data
export function getContentTypeDisplayTextFromKey( key: string | undefined | null ): string {
    switch ( key ) {
        case articleKey:
            return "Article";
        case tradeAlertKey:
            return "Trade Alert";
        case bonusReportKey:
            return "Bonus Report";
        case webinarKey:
            return "Webinar";
        case null:
        case '':
        default:
            return "Article XXX";
    }
}
